import { initSentry } from './sentry';
declare global {
  interface Window {
    posthog?: any;
    __nxtrev_analytics_consent?: boolean;
    __nxtrev_init_analytics?: () => void;
    __nxtrev_grant_analytics_consent?: () => void;
  }
}

const consentKey = 'nxtrev_cookie_consent';

function triggerSentryTestError() {
  if (new URLSearchParams(window.location.search).has('sentry-test')) {
    setTimeout(() => {
      throw new Error('NxtRev Sentry test error');
    }, 0);
  }
}

const posthogKey = import.meta.env.VITE_POSTHOG_KEY || '';
const posthogHost = import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com';

let posthogReady = false;
let advancedAnalyticsEnabled = false;


function safeText(value: string | null | undefined) {
  return (value || '').replace(/\s+/g, ' ').trim().slice(0, 80);
}

function safeHref(value: string | null | undefined) {
  if (!value) return '';

  try {
    const url = new URL(value, window.location.origin);
    return url.origin === window.location.origin ? url.pathname + url.hash : url.hostname;
  } catch {
    return '';
  }
}

function capture(eventName: string, properties: Record<string, unknown> = {}) {
  if (!posthogReady || !window.posthog?.capture) return;

  window.posthog.capture(eventName, {
    path: window.location.pathname,
    ...properties,
  });
}


function installPostHogSnippet() {
  if (window.posthog?.__SV) return;

  !function(t: Document, e: any) {
    var o: string[], n: number, p: HTMLScriptElement, r: Element;
    e.__SV || (window.posthog = e, e._i = [], e.init = function(i: string, s: any, a?: string) {
      function g(t: any, e: string) {
        var o = e.split('.');
        if (o.length === 2) {
          t = t[o[0]];
          e = o[1];
        }
        t[e] = function() {
          t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
        };
      }
      p = t.createElement('script');
      p.type = 'text/javascript';
      p.crossOrigin = 'anonymous';
      p.async = true;
      p.src = s.api_host.replace('.i.posthog.com', '-assets.i.posthog.com') + '/static/array.js';
      r = t.getElementsByTagName('script')[0];
      r.parentNode?.insertBefore(p, r);
      var u = e;
      if (a !== undefined) {
        u = e[a] = [];
      } else {
        a = 'posthog';
      }
      u.people = u.people || [];
      u.toString = function(t?: unknown) {
        var e = 'posthog';
        if (a !== 'posthog') e += '.' + a;
        if (!t) e += ' (stub)';
        return e;
      };
      u.people.toString = function() {
        return u.toString(1) + '.people (stub)';
      };
      o = 'init capture register register_once register_for_session unregister unregister_for_session getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey getNextSurveyStep identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty createPersonProfile opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing debug'.split(' ');
      for (n = 0; n < o.length; n++) g(u, o[n]);
      e._i.push([i, s, a]);
    }, e.__SV = 1);
  }(document, window.posthog || []);
}

function initAnonymousPostHog() {
  if (!posthogKey || posthogReady) return;

  installPostHogSnippet();

  window.posthog?.init?.(posthogKey, {
    api_host: posthogHost,
    person_profiles: 'never',
    persistence: 'memory',
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    disable_session_recording: true,
    advanced_disable_decide: true,
    loaded: function(posthog) {
      posthog.register({
        environment: import.meta.env.MODE,
      });
      posthogReady = true;
      capturePageView();
      if (hasAnalyticsConsent()) {
        enableAdvancedAnalytics();
      }
    },
  });
}

function hasAnalyticsConsent() {
  return (
    window.__nxtrev_analytics_consent === true ||
    window.localStorage.getItem(consentKey) === 'accepted'
  );
}

function enableAdvancedAnalytics() {
  if (!posthogReady || advancedAnalyticsEnabled || !window.posthog?.set_config) return;

  advancedAnalyticsEnabled = true;
  window.posthog.set_config({
    persistence: 'localStorage+cookie',
    autocapture: true,
    disable_session_recording: false,
    advanced_disable_decide: false,
    session_recording: {
      maskAllInputs: true,
    },
  });

  window.posthog.startSessionRecording?.();
}

function capturePageView() {
  capture('page_view', {
    title: document.title,
    referrer: document.referrer ? safeHref(document.referrer) : '',
  });
}

function trackContactFormOpened() {
  const contactForm = document.getElementById('nxtrevContactForm');
  if (!contactForm) return;

  let captured = false;
  const captureOnce = () => {
    if (captured) return;
    captured = true;
    capture('contact_form_opened');
  };

  captureOnce();
  contactForm.addEventListener('focusin', captureOnce, { once: true });
}

function trackInteractions() {
  document.addEventListener('click', (event) => {
    const target = event.target as Element | null;
    if (!target) return;

    const clickable = target.closest('a, button') as HTMLAnchorElement | HTMLButtonElement | null;
    if (!clickable) return;

    const text = safeText(clickable.textContent);
    const href = clickable instanceof HTMLAnchorElement ? clickable.getAttribute('href') : '';
    const className = clickable.getAttribute('class') || '';

    
    capture('button_clicked', {
      text,
      href: safeHref(href),
    });
    if (href?.includes('wa.me') || className.includes('whatsapp')) {
      capture('whatsapp_cta_clicked', { text, href: safeHref(href) });
      return;
    }

    if (/book|consultation|free call|get started|begin free trial/i.test(text)) {
      capture('consultation_booked', { text, href: safeHref(href) });
      return;
    }

    const serviceElement = target.closest(
      '[data-service], [data-tab], [data-ind], .service-row, a[href*="services"]'
    ) as HTMLElement | null;

    if (serviceElement) {
      capture('service_clicked', {
        service:
          serviceElement.getAttribute('data-service') ||
          serviceElement.getAttribute('data-tab') ||
          serviceElement.getAttribute('data-ind') ||
          text,
        href: safeHref(href),
      });
    }
  });
}

function trackFormSubmission() {
  document.addEventListener('submit', (event) => {
    const form = event.target as HTMLFormElement | null;
    if (form?.id === 'nxtrevContactForm') {
      capture('contact_form_submitted');
    }
  });
}

window.__nxtrev_grant_analytics_consent = enableAdvancedAnalytics;
window.__nxtrev_init_analytics = enableAdvancedAnalytics;

initSentry();
initAnonymousPostHog();
triggerSentryTestError();
trackContactFormOpened();
trackInteractions();
trackFormSubmission();

export {};
